import type { LibraryBlockTypeForListPage } from '@ContractBuilder/types';
import type { BaseUser, Team } from '@root/@types/types';
import { isClauseBlock } from '@root/helpers/blocks';
import Nav from '@root/src/routes';
import { type CellContext, createColumnHelper } from '@tanstack/react-table';
import { BLOCK_LAYOUTS_VALUE_TO_LABEL_MAPPING } from '@User/Blocks/constants';

import { effectiveToAccessor } from './shared/effectiveToAccessor';
import {
  classOfBusinessAccessor,
  createdAtAccessor,
  createdByAccessor,
  editDisplay,
  effectiveFromAccessor,
  lastUpdatedByAccessor,
  nameAccessor,
  statusAccessor,
  teamsAccessor,
  toggleSubRowsDisplay,
  updatedAtAccessor,
  usedInTemplatesAccessor,
} from './shared';

const columnHelper = createColumnHelper<LibraryBlockTypeForListPage>();

const selectClassName = '[&>*:nth-child(2)>*:first-child]:max-w-full';

export const getClausesTableColumns = ({ user, teams }: { user?: BaseUser | null; teams?: Team[] }) => [
  toggleSubRowsDisplay(columnHelper),
  columnHelper.accessor('id', {
    id: 'id',
    enableColumnFilter: false,
    enableHiding: false,
  }),
  nameAccessor(columnHelper, 'Clause name', { order: 1, placeholder: 'All clauses' }),
  columnHelper.accessor('clause_reference', {
    id: 'clause_reference',
    header: 'Reference',
    filterFn: 'arrIncludesSome',
    meta: {
      filter: {
        order: 2,
        className: selectClassName,
        label: 'Clause reference',
        placeholder: 'All references',
      },
      headingCellClassName: 'w-full max-w-32',
    },
  }),
  columnHelper.accessor('clause_type', {
    id: 'clause_type',
    header: 'Type',
    sortingFn: 'text',
    sortUndefined: -1,
    filterFn: (row, _, filterValue) => {
      if (!filterValue?.length) {
        return true;
      }
      return isClauseBlock(row.original) && filterValue.includes(row.original.clause_type);
    },
    meta: {
      filter: {
        label: 'Clause type',
        placeholder: 'All types',
        order: 3,
        className: selectClassName,
      },
      tooltip: true,
      headingCellClassName: 'w-40',
    },
  }),
  columnHelper.accessor('type', {
    id: 'layout',
    header: 'Layout',
    cell: (cellContext: CellContext<LibraryBlockTypeForListPage, string>) =>
      BLOCK_LAYOUTS_VALUE_TO_LABEL_MAPPING.find((i) => i.value === cellContext.row.original.type)?.label ?? '',
    sortingFn: 'text',
    sortUndefined: -1,
    filterFn: (row, _, selectedFilters) => {
      if ((selectedFilters?.length ?? 0) === 0) {
        return true;
      }
      return selectedFilters.includes(
        BLOCK_LAYOUTS_VALUE_TO_LABEL_MAPPING.find((i) => i.value === row.original.type)?.label ?? '',
      );
    },
    meta: {
      filter: {
        options: BLOCK_LAYOUTS_VALUE_TO_LABEL_MAPPING.filter(({ value }) => isClauseBlock({ type: value })).map(
          ({ label }) => label,
        ),
        order: 5,
        className: selectClassName,
        placeholder: 'All layouts',
      },
      tooltip: true,
      headingCellClassName: 'w-40',
    },
  }),
  columnHelper.accessor('classification', {
    id: 'classification',
    header: 'Classification',
    meta: {
      filter: {
        label: 'Clause classification',
        placeholder: 'All classifications',
        order: 4,
        className: selectClassName,
      },
      dataCellClassName: 'group',
      headingCellClassName: 'w-40',
    },
    filterFn: (row, _, filterValue) => {
      if (!filterValue?.length) {
        return true;
      }
      return filterValue.includes(row.original.classification);
    },
  }),
  statusAccessor(columnHelper),
  usedInTemplatesAccessor(columnHelper),
  effectiveToAccessor(columnHelper),
  lastUpdatedByAccessor(columnHelper),
  updatedAtAccessor(columnHelper),
  createdByAccessor(columnHelper),
  createdAtAccessor(columnHelper),
  effectiveFromAccessor(columnHelper),
  classOfBusinessAccessor(columnHelper),
  teamsAccessor(columnHelper, teams),
  editDisplay(columnHelper, Nav.Clause, user),
];
